<template>
    <div class="fieldset-cloneable">
        <div class="fieldset-cloneable-add" v-if="showAddButton() && 'top' === addButtonPosition">
            <slot name="addButton" :add="add">
                <div class="button" v-interact="add">Weiteres Feld hinzufügen</div>
            </slot>
        </div>
        <vue-nestable v-if="nestable" :value="model" v-bind="nestableOptions" ref="nestableRef" @input="setModel" @change="emit('change', $event)">
            <template #default="{item, index}">
                <div v-if="!customDragSelector" class="flex">
                    <vue-nestable-handle :item="item" class="flex flex-vertical-middle">
                        <i class="visible-md-upward material-icons drag-handle" aria-hidden="true" role="img">open_with</i>
                    </vue-nestable-handle>
                    <div :id="getUid(item)" class="full-width fieldset-slot-container">
                        <slot name="render" :values="item" :$index="index" :is-first="!index" :is-last="isLastNestedItem(item, index)" :manage="manageMethods"/>
                    </div>
                </div>
                <slot v-else name="render" :id="getUid(item)" :values="item" :$index="index" :is-first="!index" :is-last="isLastNestedItem(item, index)" :manage="manageMethods"/>
            </template>
            <template #placeholder>&nbsp;</template>
        </vue-nestable>
        <app-draggable v-else-if="sortable" v-model="model" v-bind="dragOptions" @start="emit('start', $event)" @change="emit('change', $event)"
            item-key="news-link.id">
            <template #item="{element, index}">
                <div :class="classes" :id="element.__fieldset_uid" v-show="!element.__fieldset_hidden">
                    <div class="flex">
                        <div v-if="sortable && !customDragSelector" class="flex flex-vertical-middle">
                            <i class="visible-md-upward material-icons drag-handle" aria-hidden="true" role="img">open_with</i>
                        </div>
                        <div class="full-width fieldset-slot-container">
                            <slot name="render" :values="element" :$index="index" :is-first="!index" :is-last="index === modelValue.length - 1" :manage="manageMethods"/>
                        </div>
                    </div>
                </div>
            </template>
        </app-draggable>
        <div v-else-if="renderReversed" @start="emit('start', $event)" @change="emit('change', $event)">
            <div v-for="(values, $index) in modelValueToReversed" :key="getUid(values)" :class="classes" :id="values.__fieldset_uid" v-show="!values.__fieldset_hidden">
                <div class="flex">
                    <div class="full-width fieldset-slot-container">
                        <slot name="render" :values="values" :$index="getReversedIndex($index)" :is-first="!getReversedIndex($index)"
                            :is-last="getReversedIndex($index) === modelValue.length - 1" :manage="manageMethods"/>
                    </div>
                </div>
            </div>
        </div>
        <div v-else @start="emit('start', $event)" @change="emit('change', $event)">
            <div v-for="(values, $index) in modelValue" :key="getUid(values)" :class="classes" :id="values.__fieldset_uid" v-show="!values.__fieldset_hidden">
                <div class="flex">
                    <div class="full-width fieldset-slot-container">
                        <slot name="render" :values="values" :$index="$index" :is-first="!$index" :is-last="$index === modelValue.length - 1" :manage="manageMethods"/>
                    </div>
                </div>
            </div>
        </div>

        <slot name="subContent"/>

        <div class="fieldset-cloneable-add" v-if="showAddButton() && 'bottom' === addButtonPosition">
            <slot name="addButton" :add="add">
                <div class="button" v-interact="add">Weiteres Feld hinzufügen</div>
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {queryFocusableElements} from '@/library/focus';
    import {VueNestable, VueNestableHandle} from 'vue3-nestable';
    import {uniqueId} from '@/library/string';
    import {swap} from '@/library/utils';
    import {deepCopyObject} from '@/utils/portuna';
    import AppDraggable from './AppDraggable.vue';
    import FormRow from './form/FormRow.vue';
    import {useStore} from 'vuex';
    import {ref, nextTick, computed, watch, type ComponentPublicInstance, toRefs} from 'vue';

    const portuna = usePortuna();

    type ValueType = Merge<IFieldsetValueType, Record<string, number | boolean | string | any[]>>;

    type ValueTypeNested = ValueType & {
        children?: ValueTypeNested[];
    };

    type NestedFieldsetResult = {
        parent: ValueTypeNested[];
        index: number;
        path: number[];
    };

    const emit = defineEmits<{
        (eventName: 'change' | 'start', data: any | any[]): void;
        (eventName: 'update:modelValue', data: ValueType[]): void;
    }>();

    const props = withDefaults(
        defineProps<{
            modelValue: ValueType[];
            defaultValues?: Record<string, any> | (() => any);
            sortable?: boolean;
            customClasses?: string[];
            nestable?: boolean;
            nestableOptions?: Record<string, any>;
            group?: string;
            noDefaultInputField?: boolean;
            customDragSelector?: boolean | string;
            addButtonPosition?: 'top' | 'bottom';
            focusFirstInput?: boolean;
            renderReversed?: boolean;
        }>(),
        {
            sortable: false,
            defaultValues: () => ({}),
            customClasses: () => ([]),
            nestable: false,
            nestableOptions: () => ({}),
            group: '',
            noDefaultInputField: false,
            customDragSelector: false,
            addButtonPosition: 'bottom',
            focusFirstInput: false,
            renderReversed: false,
        },
    );

    const {
              modelValue,
          } = toRefs(props);

    const slots = defineSlots<{
        addButton(props: { add: () => void }): any;
        subContent: unknown;
        render(props: IFieldsetCloneableSlot<any>): any;
    }>();

    const store       = useStore();
    const nestableRef = ref<ComponentPublicInstance>();
    const model       = ref<ValueType[]>([] as ValueType[]);

    const getUid = (entry: any) =>
    {
        if (!entry.__fieldset_uid)
        {
            entry.__fieldset_uid = uniqueId('field-');
        }

        return entry.__fieldset_uid;
    };

    const modelValueToReversed = computed(() =>
    {
        return (modelValue.value ?? []).toReversed();
    });

    const dragOptions = computed((): {} =>
    {
        if (props.sortable)
        {
            return {
                group: props.group || portuna._uid,
                'force-fallback': true,
                handle: props.customDragSelector || '.drag-handle',
            };
        }
        else
        {
            return {};
        }
    });

    const classes = computed(() =>
    {
        let classes = ['fieldset-cloneable-group'];
        if (props.sortable)
        {
            classes.push('sortable');
        }

        if (Array.isArray(props.customClasses) && props.customClasses.length > 0)
        {
            classes.push(...props.customClasses);
        }

        return classes;
    });

    // don't use as computed since $slots are not reactive - method can be used in template without brackets just like computed vars
    const showAddButton = (): boolean =>
    {
        return typeof slots.addButton !== 'undefined';
    };

    const removeErrorsWithinElement = (fieldset: Nullable<Element>) =>
    {
        if (!fieldset)
        {
            return;
        }

        for (const formRowElement of fieldset.querySelectorAll('.form-row'))
        {
            const formRow = formRowElement.__vue__ as FormRow;
            if (formRow)
            {
                store.dispatch('form/resetError', {errorName: formRow._errorName});
            }
        }
    };

    const moveUp = ($index: number) =>
    {
        if (!props.sortable || model.value.length <= 1 || !$index)
        {
            return;
        }

        swap(model.value, $index, $index - 1);
    };

    const moveDown = ($index: number) =>
    {
        if (!props.sortable || model.value.length <= 1 || $index === model.value.length - 1)
        {
            return;
        }

        swap(model.value, $index, $index + 1);
    };

    const getDefaultValues = () =>
    {
        return deepCopyObject('function' === typeof props.defaultValues ? props.defaultValues() : props.defaultValues);
    };

    const add = () =>
    {
        const values = getDefaultValues();

        model.value.push(values);

        if (props.focusFirstInput)
        {
            nextTick(() =>
            {
                const cloneableGroup = portuna.root.value?.querySelectorAll('.fieldset-cloneable-group');
                if (!cloneableGroup || !cloneableGroup.length)
                {
                    return;
                }

                const focusableElement = queryFocusableElements(cloneableGroup[cloneableGroup.length - 1] as HTMLElement).item(0);

                if (
                    typeof focusableElement.__vue__ !== 'undefined' &&
                    typeof focusableElement.__vue__.triggerFocus === 'function'
                )
                {
                    focusableElement.__vue__.triggerFocus();
                }
                else
                {
                    focusableElement.focus();
                }
            });
        }

        return values;
    };

    const setModel = (value: ValueType[]) =>
    {
        model.value = value;
    };

    const getReversedIndex = (index: number): number =>
    {
        return model.value!.length - 1 - index;
    };

    const observeEmptySet = () =>
    {
        if (!props.noDefaultInputField && model.value?.length === 0)
        {
            add();
        }
    };

    const removeFieldsetErrors = (index: number) =>
    {
        const fieldset = document.getElementById(model.value![index].__fieldset_uid!);

        removeErrorsWithinElement(fieldset);
    };

    const removeFieldsetNestedErrors = (result: NestedFieldsetResult) =>
    {
        let fieldset: Nullable<Element>;
        // follow nested path
        if (props.customDragSelector)
        {
            let node: Nullable<Element> = nestableRef.value!.$el;
            for (let path of result.path)
            {
                node = node?.querySelector(':scope > ol.nestable-list')?.children.item(path) || null;
            }

            fieldset = node?.querySelector(':scope > .nestable-item-content') || null;
        }
        // get by fieldset-uid
        else
        {
            fieldset = document.getElementById(result.parent[result.index].__fieldset_uid!);
        }

        removeErrorsWithinElement(fieldset);
    };

    const clearFieldset = ($index: number) =>
    {
        removeFieldsetErrors($index);

        model.value[$index] = getDefaultValues();
    };

    model.value = modelValue.value!;

    observeEmptySet();

    const deleteFieldset = ($index: number) =>
    {
        if (model.value!.length === 1 && !props.noDefaultInputField)
        {
            clearFieldset(0);
        }
        else
        {
            removeFieldsetErrors($index);

            nextTick(() =>
            {
                model.value.splice($index, 1);
            });
        }
    };

    const markDelete = ($index: number) =>
    {
        if (!model.value[$index].id)
        {
            deleteFieldset($index);

            return;
        }

        model.value[$index] = Object.assign(model.value[$index], {
            delete: model.value[$index].delete ? 0 : 1,
        });
    };

    const manageMethods = computed<IFieldsetCloneableManageMethods>(() =>
    {
        if (props.nestable)
        {
            return {
                delete: deleteFieldsetNested,
                markDelete: markDelete,
                clear: clearFieldsetNested,
                moveUp: moveUpNested,
                moveDown: moveDownNested,
            };
        }

        return {
            add: add,
            delete: deleteFieldset,
            markDelete: markDelete,
            clear: clearFieldset,
            moveUp: moveUp,
            moveDown: moveDown,
        };
    });

    const moveUpNested = (item: ValueTypeNested) =>
    {
        const result = findNestedFieldset(model.value, item);
        if (!result || result.parent.length <= 1 || !result.index)
        {
            return;
        }

        swap(result.parent, result.index, result.index - 1);
    };

    const moveDownNested = (item: ValueTypeNested) =>
    {
        const result = findNestedFieldset(model.value, item);
        if (!result || result.parent.length <= 1 || result.index === result.parent.length - 1)
        {
            return;
        }

        swap(result.parent, result.index, result.index + 1);
    };

    const findNestedFieldset = (
        values: ValueTypeNested[],
        search: ValueTypeNested,
        path: number[] = [],
    ): NestedFieldsetResult | false =>
    {
        let result: NestedFieldsetResult | false = false;
        for (let key in values)
        {
            if (!values.hasOwnProperty(key))
            {
                continue;
            }

            if (values[key] === search)
            {
                result = {
                    parent: values,
                    index: parseInt(key),
                    path: [...path, parseInt(key)],
                };

                break;
            }

            if (Array.isArray(values[key].children))
            {
                result = findNestedFieldset(values[key].children!, search, [...path, parseInt(key)]);
                if (result)
                {
                    break;
                }
            }
        }

        return result;
    };

    const deleteFieldsetNested = (item: ValueTypeNested) =>
    {
        const result = findNestedFieldset(model.value, item);
        if (result)
        {
            if (result.parent === model.value && model.value.length === 1)
            {
                clearFieldset(0);
            }
            else
            {
                removeFieldsetNestedErrors(result);

                nextTick(() => result.parent.splice(result.index, 1));
            }
        }
    };

    const clearFieldsetNested = (item: ValueTypeNested) =>
    {
        const result = findNestedFieldset(model.value, item);
        if (result)
        {
            removeFieldsetNestedErrors(result);

            result.parent[result.index] = Object.assign(getDefaultValues(), item.children ? {children: item.children} : {});
        }
    };

    const isLastNestedItem = (item: ValueTypeNested, index: number) =>
    {
        const result = findNestedFieldset(model.value, item);

        return result && index === result.parent.length - 1;
    };

    watch(modelValue, (): void =>
    {
        model.value = modelValue.value!;

        observeEmptySet();
    }, {deep: true});

    watch(model, (): void =>
    {
        emit('update:modelValue', model.value);
    }, {deep: true});

    defineExpose(Object.assign(
        {},
        portuna,
        {
            componentName: 'FieldsetCloneable',
            nestableRef,
            model,
            modelValueToReversed,
            dragOptions,
            classes,
            manageMethods,
            getUid,
            showAddButton,
            removeErrorsWithinElement,
            moveUp,
            moveDown,
            getDefaultValues,
            add,
            setModel,
            getReversedIndex,
            observeEmptySet,
            removeFieldsetErrors,
            removeFieldsetNestedErrors,
            clearFieldset,
            deleteFieldset,
            markDelete,
            moveUpNested,
            moveDownNested,
            findNestedFieldset,
            deleteFieldsetNested,
            clearFieldsetNested,
            isLastNestedItem,
        },
    ));
</script>